import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { PlanResponse, PlanSearch, RidingPreferences, RouteStats } from '../../shared/contracts.js';
import { formatDistance, formatDuration, formatExtraTime } from '../../shared/format.js';
import { PREFERENCE_LABELS } from '../preferences.js';
import { Badge, Button } from './ui.js';

const count = (value: number) => value.toLocaleString('en-NZ');
const percent = (part: number, whole: number) => whole > 0 ? `${(part / whole * 100).toFixed(part / whole < 0.1 ? 1 : 0)}%` : '0%';
const engineTime = (ms: number) => ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;
const humanize = (value: string) => value.charAt(0) + value.slice(1).toLowerCase().replaceAll('_', ' ');

const CONTROL_LABELS: Record<string, string> = {
  NONE: 'No control', UNKNOWN: 'Unknown', SIGNAL_THREE: 'Signals, 3-way junction', SIGNAL_FOUR: 'Signals, 4-way junction',
  SIGNAL_LEFT: 'Signals, left turn only', SIGNAL_OTHER: 'Signals, other', STOP: 'Stop sign', GIVE_WAY: 'Give way', SUPPRESSED_DUPLICATE: 'Duplicate (not charged)'
};
const CONTROL_ORDER = Object.keys(CONTROL_LABELS);
const SIGNALS = ['SIGNAL_THREE', 'SIGNAL_FOUR', 'SIGNAL_LEFT', 'SIGNAL_OTHER'];
const SEARCH_LABELS: Record<PlanSearch['kind'], string> = { baseline: 'Neutral route', candidate: 'Riding settings', return: 'Return section' };

const total = (counts: Record<string, number>, keys: string[]) => keys.reduce((sum, key) => sum + (counts[key] ?? 0), 0);
const junctionsPassed = (stats: RouteStats) => stats.junctions.threeWay + stats.junctions.fourWay + stats.junctions.fivePlus;
const turnsTaken = (stats: RouteStats) => stats.junctions.left + stats.junctions.right + stats.junctions.uTurn;

function Section({ title, children, note }: { title: string; children: ReactNode; note?: string }) {
  return <section className="debug-section"><h3>{title}</h3>{children}{note ? <p className="debug-note">{note}</p> : null}</section>;
}

function Tiles({ items }: { items: Array<[string, string]> }) {
  return <div className="debug-tiles">{items.map(([label, value]) => <div className="debug-tile" key={label}><strong>{value}</strong><span>{label}</span></div>)}</div>;
}

/** Bars of metres (the default) or of plain counts. A fixed `order` keeps a known list in reading order. */
function Bars({ title, counts, whole, label = humanize, order, unit = 'distance' }: {
  title: string; counts: Record<string, number>; whole: number; label?: (key: string) => string; order?: string[]; unit?: 'distance' | 'count';
}) {
  const keys = Object.keys(counts).filter((key) => (counts[key] ?? 0) > 0).sort((a, b) => order ? order.indexOf(a) - order.indexOf(b) : counts[b]! - counts[a]!);
  return (
    <div>
      <h4>{title}</h4>
      <ul className="debug-bars">{keys.map((key) => (
        <li key={key}>
          <span title={label(key)}>{label(key)}</span>
          <span className="debug-bar"><span style={{ width: percent(counts[key]!, whole) }} /></span>
          <strong>{unit === 'count' ? count(counts[key]!) : `${formatDistance(counts[key]!)} · ${percent(counts[key]!, whole)}`}</strong>
        </li>
      ))}</ul>
    </div>
  );
}

function searchSetting(search: PlanSearch): string {
  if (search.kind === 'candidate' && search.preferences) return `${search.preferences.winding} / ${search.preferences.motorways} / ${search.preferences.trafficLights}`;
  if (search.kind === 'return' && search.preference !== undefined) return `${search.preference > 0 ? '+' : ''}${search.preference} (×${search.multiplier})`;
  return '—';
}

function searchOutcome(search: PlanSearch): ReactNode {
  if (search.error) return <span className="debug-error" title={search.error}>{search.error.split(':')[0]}</span>;
  if (search.kind === 'baseline') return 'reference';
  return search.fits ? 'fits' : 'over allowance';
}

/** The neutral route and the chosen one side by side: what the riding settings changed. */
function comparison(plan: PlanResponse): Array<[string, string, string]> {
  const { baseline, route } = plan.debug;
  const rows: Array<[string, string, string]> = [
    ['Distance', formatDistance(baseline.meters), formatDistance(plan.route.distanceMeters)],
    ['Riding time', formatDuration(baseline.seconds), formatDuration(plan.route.timeSeconds)]
  ];
  const [from, to] = [baseline.stats, route.stats];
  if (!from || !to) return rows;
  const km = (stats: RouteStats, meters: number) => `${formatDistance(meters)} · ${percent(meters, stats.meters)}`;
  const both = (read: (stats: RouteStats) => string): [string, string] => [read(from), read(to)];
  const lines: Array<[string, [string, string]]> = [
    ['Graph edges', both((stats) => count(stats.edges))],
    ['Traffic signals', both((stats) => count(total(stats.controls, SIGNALS)))],
    ['Stop signs and give ways', both((stats) => count(total(stats.controls, ['STOP', 'GIVE_WAY'])))],
    ['Junctions passed', both((stats) => count(junctionsPassed(stats)))],
    ['Turns at junctions', both((stats) => count(turnsTaken(stats)))],
    ['Changes of OSM way', both((stats) => count(stats.wayChanges))],
    ['Motorway', both((stats) => km(stats, stats.roadClass.MOTORWAY ?? 0))],
    ['Straight (curvature ≥ 0.95)', both((stats) => km(stats, stats.curvature.straight))],
    ['Winding (curvature < 0.85)', both((stats) => km(stats, stats.curvature.winding))]
  ];
  return [...rows, ...lines.map(([label, [a, b]]): [string, string, string] => [label, a, b])];
}

export function DebugPanel({ plan, requested, outdated, onClose }: { plan: PlanResponse; requested: RidingPreferences; outdated: boolean; onClose: () => void }) {
  const closeButton = useRef<HTMLButtonElement>(null);
  const [copied, setCopied] = useState<'idle' | 'done' | 'failed'>('idle');
  useEffect(() => {
    closeButton.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { debug } = plan;
  const stats = debug.route.stats;
  const searches = debug.searches;
  const visited = searches.map((search) => search.visitedNodes ?? 0);
  const returnSearch = searches.find((search) => search.kind === 'return');
  const turnaround = plan.returnRoute?.turnaroundPointId ? debug.stops.find((stop) => stop.id === plan.returnRoute!.turnaroundPointId)?.label : undefined;
  const copy = async () => {
    const { planId, appliedPreferences, allowanceSeconds, extraSeconds, returnRoute } = plan;
    try {
      await navigator.clipboard.writeText(JSON.stringify({ planId, requested, appliedPreferences, allowanceSeconds, extraSeconds, returnRoute, debug }, null, 2));
      setCopied('done');
    } catch {
      setCopied('failed');
    }
    setTimeout(() => setCopied('idle'), 1800);
  };

  return (
    <div className="debug-backdrop" role="dialog" aria-modal="true" aria-label="Route internals" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <article className="debug-dialog">
        <header className="debug-header">
          <div>
            <p className="eyebrow">Route internals</p>
            <h2>Plan {plan.planId}</h2>
          </div>
          <div className="debug-header-actions">
            {outdated ? <Badge tone="warning">Outdated</Badge> : null}
            <Button type="button" variant="outline" onClick={copy}>{copied === 'done' ? 'Copied' : copied === 'failed' ? 'Copy failed' : 'Copy JSON'}</Button>
            <button ref={closeButton} type="button" className="button button-outline" onClick={onClose}>Close <kbd>Esc</kbd></button>
          </div>
        </header>

        <div className="debug-body">
          <Section title="Engine work" note="Visited nodes are the routing engine's search effort (GraphHopper's visited_nodes.sum), summed over a search's legs. A leg that passes the engine's limit fails that search.">
            <Tiles items={[
              ['server time', engineTime(debug.durationMs)],
              ['engine searches', count(searches.length)],
              ['engine time', engineTime(searches.reduce((sum, search) => sum + search.ms, 0))],
              ['nodes visited, all searches', count(visited.reduce((sum, nodes) => sum + nodes, 0))],
              ['nodes visited, busiest search', count(Math.max(0, ...visited))],
              ['points in the drawn line', count(debug.route.points)],
              ...(debug.route.distinctWays === undefined ? [] : [['distinct OSM ways', count(debug.route.distinctWays)] as [string, string]]),
              ['extra time used', `${formatExtraTime(plan.extraSeconds)} of ${formatDuration(plan.allowanceSeconds)}`]
            ]} />
            <div className="debug-table-scroll">
              <table className="debug-table">
                <thead><tr><th>#</th><th>Search</th><th title="Winding / motorways / traffic lights, or the signed return preference">Setting</th><th className="num">Engine</th><th className="num">Nodes visited</th><th className="num">Whole itinerary</th><th>Result</th></tr></thead>
                <tbody>{searches.map((search, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td><td>{SEARCH_LABELS[search.kind]}</td><td>{searchSetting(search)}</td>
                    <td className="num">{engineTime(search.ms)}</td>
                    <td className="num">{search.visitedNodes === undefined ? '—' : count(search.visitedNodes)}</td>
                    <td className="num">{search.seconds === undefined ? '—' : `${formatDuration(search.seconds)} (${formatExtraTime(search.seconds - debug.baseline.seconds)})`}</td>
                    <td>{searchOutcome(search)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Section>

          <Section title="Neutral route and this route" note={stats ? 'Counts are per graph edge, the road pieces between two routing nodes. The neutral route is the baseline all riding settings are measured against.' : 'The routing engine did not send edge details, so only the headline figures are available.'}>
            <div className="debug-table-scroll">
              <table className="debug-table">
                <thead><tr><th /><th className="num">Neutral route</th><th className="num">This route</th></tr></thead>
                <tbody>{comparison(plan).map(([label, neutral, chosen]) => <tr key={label}><td>{label}</td><td className="num">{neutral}</td><td className="num">{chosen}</td></tr>)}</tbody>
              </table>
            </div>
          </Section>

          {stats ? <>
            <Section title="Road makeup" note="Curvature is the engine's beeline-over-length ratio per edge, the same bands the winding preference scores. Short edges on twisty roads can read as straight (#10).">
              <div className="debug-columns">
                <Bars title="Road class" counts={stats.roadClass} whole={stats.meters} />
                <Bars title="Surface" counts={stats.surface} whole={stats.meters} />
                <Bars title="Curvature" counts={{ straight: stats.curvature.straight, gentle: stats.curvature.gentle, winding: stats.curvature.winding }} whole={stats.meters}
                  label={(key) => ({ straight: 'Straight (≥ 0.95)', gentle: 'Gentle (0.85–0.95)', winding: 'Winding (< 0.85)' })[key] ?? key} />
                <Bars title="Road environment" counts={stats.environment} whole={stats.meters} />
              </div>
            </Section>

            <Section title="Controls and junctions" note="Control is what the rider meets on arriving at the end of an edge; Unknown means the graph has no control information there. A junction is a node with three or more roads meeting; roads the car profile cannot use are not in the graph. “Choice” means at least two drivable exits.">
              <div className="debug-columns">
                <Bars title="Controls (edges)" counts={stats.controls} whole={stats.edges} label={(key) => CONTROL_LABELS[key] ?? humanize(key)} order={CONTROL_ORDER} unit="count" />
                <Bars title="Junctions passed" whole={junctionsPassed(stats)} unit="count" order={['threeWay', 'fourWay', 'fivePlus', 'withChoice']}
                  counts={{ threeWay: stats.junctions.threeWay, fourWay: stats.junctions.fourWay, fivePlus: stats.junctions.fivePlus, withChoice: stats.junctions.withChoice }}
                  label={(key) => ({ threeWay: '3 roads', fourWay: '4 roads', fivePlus: '5 or more roads', withChoice: 'Offered a choice' })[key] ?? key} />
                <Bars title="Movement at junctions" whole={junctionsPassed(stats)} unit="count" order={['straight', 'left', 'right', 'uTurn']}
                  counts={{ straight: stats.junctions.straight, left: stats.junctions.left, right: stats.junctions.right, uTurn: stats.junctions.uTurn }}
                  label={(key) => ({ straight: 'Straight on', left: 'Turned left', right: 'Turned right', uTurn: 'Turned back' })[key] ?? key} />
              </div>
            </Section>
          </> : null}

          <Section title="Riding settings">
            <div className="debug-table-scroll">
              <table className="debug-table">
                <thead><tr><th /><th className="num">Requested</th><th className="num">Applied</th></tr></thead>
                <tbody>{PREFERENCE_LABELS.map(({ key, title }) => <tr key={key}><td>{title}</td><td className="num">{requested[key]}</td><td className="num">{plan.appliedPreferences[key]}</td></tr>)}</tbody>
              </table>
            </div>
            <p className="debug-note">{plan.message}</p>
          </Section>

          {plan.returnRoute ? (
            <Section title="Return-road preference">
              <Tiles items={[
                ['requested', String(plan.returnRoute.requestedPreference)],
                ['applied', String(plan.returnRoute.appliedPreference)],
                ['eligible', plan.returnRoute.eligible ? 'yes' : 'no'],
                ['turnaround stop', turnaround ?? '—'],
                ...(returnSearch?.roadIds === undefined ? [] : [['outward road IDs sent', count(returnSearch.roadIds)] as [string, string]]),
                ...(returnSearch?.multiplier === undefined ? [] : [['penalty multiplier', String(returnSearch.multiplier)] as [string, string]])
              ]} />
              <p className="debug-note">{plan.returnRoute.message}</p>
            </Section>
          ) : null}

          {debug.route.legs ? (
            <Section title="Legs">
              <div className="debug-table-scroll">
                <table className="debug-table">
                  <thead><tr><th>#</th><th>From</th><th>To</th><th className="num">Distance</th><th className="num">Time</th><th className="num">Edges</th><th className="num">OSM ways</th></tr></thead>
                  <tbody>{debug.route.legs.map((leg, index) => (
                    <tr key={index}><td>{index + 1}</td><td>{leg.from}</td><td>{leg.to}</td><td className="num">{formatDistance(leg.meters)}</td>
                      <td className="num">{formatDuration(leg.seconds)}</td><td className="num">{leg.edges === undefined ? '—' : count(leg.edges)}</td><td className="num">{count(leg.ways)}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            </Section>
          ) : null}

          <Section title="Stops" note="How far the engine moved each place to reach a drivable road.">
            <div className="debug-table-scroll">
              <table className="debug-table">
                <thead><tr><th>#</th><th>Place</th><th className="num">Moved</th></tr></thead>
                <tbody>{debug.stops.map((stop, index) => <tr key={`${stop.id}-${index}`}><td>{index + 1}</td><td>{stop.label}</td><td className="num">{stop.snapMeters === undefined ? '—' : formatDistance(stop.snapMeters)}</td></tr>)}</tbody>
              </table>
            </div>
          </Section>
        </div>
      </article>
    </div>
  );
}
